import { useCallback, useEffect, useRef, useState } from 'react'
import { PreviewFile } from 'config/types'
import useUploadService from 'hooks/useUploadService'
import { useFileValidation } from 'hooks/useFileValidation'
import { getFileExtension } from 'utils'
import { rememberFileMetadata } from 'components/UploadedFileList/fileMetadata'

export interface ExecutionCommentAttachment extends PreviewFile {
  // The local draft retains the job when the user switches to another result.
  upload?: Promise<string>
  error?: boolean
}

export const executionAttachmentKind = (file: File) => {
  if (/^image\//.test(file.type) || /\.(jpe?g|png|gif|webp|heic)$/i.test(file.name)) return 'image'
  if (/^video\//.test(file.type) || /\.(mp4|mov|webm|m4v|avi|mkv)$/i.test(file.name)) return 'video'
  return 'file'
}

export function useExecutionCommentAttachments(
  initial: ExecutionCommentAttachment[],
  onChange: (items: ExecutionCommentAttachment[]) => void
) {
  const [attachments, setAttachments] = useState(initial)

  const mounted = useRef(true)
  const current = useRef(attachments)
  const changed = useRef(onChange)

  changed.current = onChange

  const { imageFileUpload, uploadFile } = useUploadService()
  const { fileSizeValidation } = useFileValidation()

  const update = useCallback((next: ExecutionCommentAttachment[]) => {
    current.current = next
    if (mounted.current) setAttachments(next)
    changed.current(next)
  }, [])

  const start = (item: ExecutionCommentAttachment): ExecutionCommentAttachment => ({
    ...item,
    error: false,
    uploadedUrl: undefined,
    upload: (executionAttachmentKind(item.file) === 'image'
      ? imageFileUpload(item.file)
      : uploadFile(item.file, getFileExtension(item.file.name))
    )
      .then((url) => {
        if (url && executionAttachmentKind(item.file) === 'file') rememberFileMetadata(url, item.file)
        return url
      })
      .catch(() => ''),
  })

  const add = (files: File[]) => {
    const added = files.filter(fileSizeValidation).map((file) => start({ file, previewUrl: URL.createObjectURL(file) }))

    if (added.length) update([...current.current, ...added])
  }

  const remove = (item: ExecutionCommentAttachment) => {
    URL.revokeObjectURL(item.previewUrl)
    update(current.current.filter((entry) => entry !== item))
  }

  const retry = (item: ExecutionCommentAttachment) => {
    if (!item.error) return
    update(current.current.map((entry) => (entry === item ? start(entry) : entry)))
  }

  const clear = useCallback(() => {
    current.current.forEach((item) => URL.revokeObjectURL(item.previewUrl))
    update([])
  }, [update])

  useEffect(
    () => () => {
      mounted.current = false
    },
    []
  )

  useEffect(() => {
    let active = true

    attachments.forEach((item) => {
      item.upload?.then((url) => {
        if (!active || !current.current.includes(item)) return
        update(
          current.current.map((entry) =>
            entry === item ? { ...entry, upload: undefined, uploadedUrl: url || undefined, error: !url } : entry
          )
        )
      })
    })
    return () => {
      active = false
    }
  }, [attachments, update])

  return {
    attachments,
    add,
    remove,
    retry,
    clear,
    ready: attachments.every((item) => !!item.uploadedUrl && !item.upload && !item.error),
  }
}
